import {
  PROJECT,
  FEEDERLISTS,
  ISSUE,
  MAINTAB,
  USER,
  QMS,
  USER_PREFERENCES,
} from "../_constants/actionConstants";
import { ADR, REPORT } from "../_constants/actionConstants";
import { DOCTYPE } from "../_constants/docTypeConstants";
import adrActions from "./adrActions";
import { getUUID } from "../_utils/generalUtils";
import raiseError from "./raiseError";

const _msgInvalidArgs = (funcName = "unknown func") =>
  `Invalid argument(s) provided for '${funcName}'. ` +
  "Please contact the system administrator.";

/**
 * Checks out the reserved tabId and adds a new tab to redux state.
 * If the checkout token is not associated with any reserved tabId's,
 * do nothing.
 *
 * @param {Object} tab - Tab object loaded with idCheckoutToken
 */
const addTab = (tab) => (dispatch, getState) => {
  const state = getState();
  const tabId = state.mainTabs.reservedTabIds[tab.idCheckoutToken];
  const openTabs = state.mainTabs.openTabs;

  if (!tabId) return;

  if (!tab.projectId) {
    const existingTab = openTabs.find(
      (t) =>
        t.docType === tab.docType &&
        !t.projectId
    );

    if (existingTab) {
      dispatch(selectTab(existingTab.tabId));
      return existingTab.tabId;
    }
  }

  dispatch({ type: MAINTAB.APPLY_TAB_ID, idCheckoutToken: tab.idCheckoutToken });
  const newTab = { ...tab, tabId, link: "#" };
  dispatch({ type: MAINTAB.ADD, tab: newTab });
  dispatch(selectTab(newTab.tabId));

  return newTab.tabId;
};

/**
 * Reserves a tabId prior to creating a new tab. New tabId is
 * obtained by finding the (max + 1) value from both openTabs' tabId's and
 * currently reserved tabIds - this eliminates any potential
 * duplication of tabIds.
 *
 * @returns {String} - UUID checkout key
 */
const reserveTabId = () => (dispatch, getState) => {
  // Find the max value of reservedTabIds. If it's empty, return 0 (an empty array
  // will yield '-Infinity' for this operation)
  const maxCurrReserved =
    Object.keys(getState().mainTabs.reservedTabIds).length > 0
      ? Math.max.apply(Math, Object.values(getState().mainTabs.reservedTabIds))
      : 0;

  // Find the max value of currently open tabs.
  const maxCurrOpenTabs = Math.max.apply(
    Math,
    getState().mainTabs.openTabs.map((t) => t.tabId)
  );

  // Find the max between reservedTabIds and tabIds from openTabs
  const tabId = Math.max(maxCurrReserved, maxCurrOpenTabs) + 1;

  // Generate the UUID checkout token
  const idCheckoutToken = getUUID();

  dispatch({ type: MAINTAB.RESERVE_TAB_ID, tabId, idCheckoutToken });

  // Issue the token to the caller
  return idCheckoutToken;
};

const unreserveTabId = (idCheckoutToken) => ({
  type: MAINTAB.UNRESERVE_TAB_ID,
  idCheckoutToken,
});

/**
 * Closes the requested tab, and selects the tab with next highest tabId.
 *
 * @param {number} tabId
 */
const closeTab = (tabId) => (dispatch, getState) => {
  // Select the last visible tab upon close
  // 3 Cases:
  // 1) Last visible tab is closed: select openTabs[openTabs.length - 2].tabId
  // 2) Middle tab is closed: select openTabs[openTabs.length - 1].tabId
  // 3) Above 2 cases should only be reachable when openTabs.length > 0.
  //    Otherwise simply select the home tab (tabId = 0).
  let tabIndex = 0;
  const openTabs = getState().mainTabs.openTabs;
  const tab = openTabs.find((t) => t.tabId === tabId);

  if (!tab) {
    raiseError(null, null, _msgInvalidArgs("closeTab"));
    dispatch(selectTab(openTabs[0])); // Select home
    return;
  }

  // Home tab is always open by default, so check if >1
  if (openTabs.length > 1) {
    if (openTabs[openTabs.length - 1].tabId === tab.tabId) tabIndex = openTabs.length - 2;
    if (tab.tabId < openTabs[openTabs.length - 1].tabId && tab.tabId > 0)
      tabIndex = openTabs.length - 1;
  } else tabIndex = 0;

  // 1. Remove tab from openTabs
  dispatch({ type: MAINTAB.CLOSE, tab });
  dispatch(selectTab(openTabs[tabIndex].tabId));

  // 2. Remove any info related to the tab from redux store.
  //    (i.e. any search results indexed with the tab's searchId,
  //    or openAdrs with the tabId)
  switch (tab.docType) {
    case DOCTYPE.ADR:
      dispatch(adrActions.deleteTempAttachments(tab.tabId)).then(() =>
        dispatch({ type: ADR.CLOSE_ADR, tabId: tab.tabId })
      );
      break;

    case DOCTYPE.ADR_LIST:
      dispatch({
        type: ADR.CLOSE_ADR_LIST,
        tabId: tab.tabId,
      });
      break;

    case DOCTYPE.ADR_SEARCH_RESULTS:
      dispatch({
        type: ADR.CLOSE_SEARCH,
        tabId: tab.tabId,
      });
      break;
    case DOCTYPE.REPORT_SELECTION:
    case DOCTYPE.REPORT_LABOR_WEEKLY:
    case DOCTYPE.REPORT_CONTRACTOR_WEEKLY:
    case DOCTYPE.REPORT_ITEM_EST_QUANTITIES:
    case DOCTYPE.REPORT_COMMENT_KEYWORD:
    case DOCTYPE.REPORT_WORK_PERFORMED:
      dispatch({ type: REPORT.CLOSE, tabId: tab.tabId });
      break;

    case DOCTYPE.ADMIN_TASKS:
      dispatch({ type: USER.CLEAR_USER_LIST, tabId: tab.tabId });
      break;

    case DOCTYPE.ISSUE_THREAD:
      dispatch({
        type: ISSUE.CLOSE_ISSUE_TAB,
        tabId: tab.tabId,
      });
      break;

    case DOCTYPE.QMS_CHANGE_MANAGEMENT_TRACKER_LIST:
      dispatch({ type: QMS.ISSUE.CLEAR_LIST, tabId: tab.tabId });
      break;

    case DOCTYPE.USER_PREFERENCES:
      dispatch({ type: USER_PREFERENCES.CLEAR_MISC_SESSION_DATA, tabId: tab.tabId });
      break;

    default:
      break;
  }
};

const closeAllTabs = () => {
  return function (dispatch) {
    dispatch({ type: MAINTAB.CLOSE_ALL });
    dispatch({ type: REPORT.CLEAR_ALL_REPORT_DATA });
    dispatch({ type: ADR.CLEAR_ALL_ADR_DATA });
    dispatch({ type: PROJECT.CLEAR_ALL_PROJECT_DATA });
    dispatch({ type: FEEDERLISTS.CLEAR_ALL });
  };
};

const markTabContentDirty = (tabId) => (dispatch) =>
  dispatch({ type: MAINTAB.MARK_DIRTY, tabId });

const unmarkTabContentDirty = (tabId) => (dispatch) =>
  dispatch({ type: MAINTAB.UNMARK_DIRTY, tabId });

const selectTab = (tabId) => (dispatch) => dispatch({ type: MAINTAB.SELECT, tabId });

const changeTabLabel = (tabId, newLabel) => (dispatch) =>
  dispatch({ type: MAINTAB.CHANGE_LABEL, tabId, newLabel });

const changeMetadata = (tabId, tabData) => (dispatch) =>
  dispatch({ type: MAINTAB.CHANGE_METADATA, tabId, tabData });

const updateTab = (tabId, tabData) => (dispatch) =>
  dispatch({ type: MAINTAB.CHANGE_METADATA, tabId, tabData });

// Organize interface alphabetically
const mainTabActions = {
  addTab,
  changeMetadata,
  changeTabLabel,
  closeAllTabs,
  closeTab,
  markTabContentDirty,
  reserveTabId,
  selectTab,
  unreserveTabId,
  unmarkTabContentDirty,
  updateTab,
};

export default mainTabActions;
